/* eslint-disable max-len */
import {
	Button,
	Form,
	Input,
	Modal,
	Upload,
	Tooltip
} from "antd"
import React, { useState } from "react"
import { FormattedMessage } from "react-intl"
import { Cross, DeleteIcon, InfoIcon, UploadIcon } from "../../icons"
import TextArea from "antd/lib/input/TextArea"
import { useParams } from "react-router-dom/cjs/react-router-dom"
import { getBase64 } from "../../helpers/base64"
import { attachmentExtensions } from "../constants/attachment-extensions"
import styles from "./contact-form.module.css"
import { showErrorNotification } from "../notification"
import { localize } from "../../utils/localize"

const { Dragger } = Upload

const sendData = async (hash, form, setLoading, setVisible) => {
	form.validateFields().then(async () => {
		const values = form.getFieldsValue()
		const attachments = []
		if (values.attachments?.fileList) {
			for (const file of values.attachments.fileList) {
				const base64 = await getBase64(file.originFileObj)
				attachments.push({ name: file.name, data: base64.split(",")[1], type: file.name.split('.').pop() })
			}
		}

		const data = {
			body: values.body,
			from: values.from,
			subject: values.subject,
			attachments
		}

		try {
			setLoading(true)
			const url = `${process.env.REACT_APP_OUTBACK_API_URL}/${process.env.REACT_APP_STAGE}/email`
			const response = await fetch(url, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"OutbackHash": hash
				},
				body: JSON.stringify(data)
			})
			const res = await response.json()
			setLoading(false)
			setVisible(false)
			form.resetFields()
			return res
		} catch (error) {
			return null
		}
	})
}

export const ContactForm = ({
	visible, setVisible
}) => {
	const { hash } = useParams()
	const [form] = Form.useForm()
	const [loading, setLoading] = useState(false)
	const [files, setFiles] = useState([])

	const onFilesChange = (e) => {
		let totalSize = 0
		const newFiles = e.fileList.filter(file => {
			const fileExtension = file.name.split(".").pop()
			if (!attachmentExtensions.includes(fileExtension.toLowerCase())) {
				showErrorNotification(localize("This extension is not allowed"))
				return false
			}
			totalSize += file.size
			if (totalSize / 1024 / 1024 > 10) {
				showErrorNotification(localize("Total size of files must be less than 10 Mb!"))
				return false
			}
			return true
		})
		setFiles(newFiles)
	}

	const dummyRequest = ({ onSuccess }) => {
		setTimeout(() => {
			onSuccess("ok")
		}, 0)
	}

	return (
		<Modal
			centered
			visible={visible}
			onCancel={() => setVisible(false)}
			closeIcon={<Cross />}
			width={640}
			title={localize("Contact Form")}
			footer={[
				<div className="footer-in-modal-window-with-double-sided-buttons" key="contact-form-footer">
					<div><FormattedMessage id="A member of our support team will get back to you shortly" /></div>
					<div>
						<Button key="share" onClick={() => sendData(hash, form, setLoading, setVisible)} type="primary" aria-label={localize("Send")}>
							<FormattedMessage id="Send" />
						</Button>
						<Button key="back" onClick={() => setVisible(false)} aria-label={localize("Cancel")}>
							<FormattedMessage id="Cancel" />
						</Button>
					</div>
				</div>
			]}
		>
			<Form
				className={loading ? "custom-loader-spinner-type" : ""}
				labelCol={{ span: 7 }}
				form={form}
			>
				<Form.Item
					name="from"
					required
					rules={[{ required: true, message: localize("Please input your email") }]}
					label={localize("From")}
				>
					<Input
						placeholder={localize("Please input your email")}
						aria-label={localize("From")}
						aria-required="true"
					/>
				</Form.Item>
				<Form.Item
					name="subject"
					required
					rules={[{ required: true, message: localize("Please input email subject") }]}
					label={localize("Subject")}
				>
					<Input
						placeholder={localize("Please input email subject")}
						aria-label={localize("Subject")}
						aria-required="true"
					/>
				</Form.Item>
				<Form.Item
					name="body"
					label={localize("Description")}
				>
					<TextArea
						placeholder={localize("Please enter the details of your request")}
						aria-label={localize("Description")}
						autoSize={{
							minRows: 6,
							maxRows: 8
						}}
					/>
				</Form.Item>
				<Form.Item
					name="attachments"
					label={(
						<>
							<FormattedMessage id="Attachments" />
							<Tooltip
								title={(
									<div className={styles.files_formats}>
										<p><b><FormattedMessage id="Following extensions are allowed:" /></b></p>
										<div>{attachmentExtensions.join(", .")}</div>
										<p><b><FormattedMessage id="Max total size is 10 Mb" /></b></p>
									</div>
								)}
								overlayClassName={styles.tooltip}
							>
								<button
									type="button"
									className={`${styles.info} small-custom-button info`}
									aria-label="Attachment file formats information"
								>
									<InfoIcon />
								</button>
							</Tooltip>
						</>
					)}
				>
					<Dragger
						name="attachments"
						multiple
						customRequest={dummyRequest}
						fileList={files}
						onChange={onFilesChange}
						beforeUpload={() => false}
						aria-label="Attachments"
						showUploadList={{
							showRemoveIcon: true,
							removeIcon: (
								<DeleteIcon />
							)
						}}
					>
						<p className="ant-upload-drag-icon">
							<UploadIcon />
						</p>
						<p className="ant-upload-text"><FormattedMessage id="Click or drag file to this area to upload" /></p>
						<p className="ant-upload-hint">
							<FormattedMessage id="Supports both single and bulk uploads." /><br />
							<b><FormattedMessage id="Important: Uploading company-confidential data or prohibited file types is strictly forbidden." /></b>
						</p>
					</Dragger>
				</Form.Item>
			</Form>
		</Modal>
	)
}
